import type { TagColor } from "@entrilia/shared/api/types";
import type { Fund, Investor } from "./commonApiTypes";

// Balance

export interface BalanceReportRequest {
  reportId: string;
  investorIds?: string[];
  fundIds?: string[];
  rowsLimit?: number;
  reportingDate: string;
}

export interface BalanceReportExportRequest extends BalanceReportRequest {
  widgetName: string;
}

export interface BalanceReportParameters {
  reportingDates: string[];
  investors: Investor[];
  funds: Fund[];
}

// Portfolio

export interface PortfolioReportRequest {
  reportId: string;
  columnsLimit?: number;
  rowsLimit?: number;
  fundId: string;
}

export interface PortfolioReportParameters {
  funds: Fund[];
}

// Cash Flows

export interface CashFlowsReportRequest {
  investorIds?: string[];
  fundIds?: string[];
  paymentStatuses?: CashFlowPaymentStatus[];
  fromDueDate?: string;
  toDueDate?: string;
  rowsLimit?: number;
  columnsLimit?: number;
  includeRowDetails?: boolean;
}

export const allCashFlowPaymentStatuses = ["Unpaid", "PartlyPaid", "Paid"] as const;

export type CashFlowPaymentStatus = (typeof allCashFlowPaymentStatuses)[number];

export interface CashFlowsReportParameters {
  investors: Investor[];
  funds: Fund[];
}

// Common

export interface DetailColumn {
  id: string;
  title: string;
  dataType: ColumnDataType;
  alignToColumnId: string;
}

export interface TabularReport {
  columns: ReportColumn[];
  rows: ReportRow[];
  groupTotals: ReportGroupTotal[];
  grandTotals?: Record<string, ReportCell>;
  hasRowsWithDetails?: boolean;
  detailColumns?: DetailColumn[];
}

export interface ReportRow {
  id: string;
  cells: Record<string, ReportCell>;
  detailRows?: ReportRow[];
}

export interface ReportColumn {
  id: string;
  name: string;
  title: string;
  dataType: ColumnDataType;
  hasStartIcon?: boolean;
  groupIndex?: number;
}

export interface ReportCell {
  formattedValue: string;
  subValue?: string;
  valueType: BiValueType;
  startIcon?: string;
  tagColor?: TagColor;
}

export interface ReportGroupTotal {
  filters: Record<string, ReportCell>;
  values: Record<string, ReportCell>;
}

type ColumnDataType = "Text" | "Numeric" | "Date" | "DateTime" | "Boolean";

type BiValueType = "String" | "Integer" | "Decimal" | "DateTime" | "Boolean" | "Unknown" | "Long";
